import React, { useState, useMemo } from "react";
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
} from "@mui/material";
import { useGetCreditHistoryQuery } from "../store/services/resellerApi";
import moment from "moment";
import { layoutCSS } from "../component/common/commonStyle";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const getTodayDate = () => new Date().toISOString().slice(0, 10);

/* ────────────────────────── Styles ────────────────────────── */
// Exact match of the delivery report's `inputSx`
const inputSx = {
  borderRadius: "0.5rem",
  minWidth: 100,
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,0.7)",
    "& fieldset": { borderColor: "#E2E8F0" },
    "&:hover fieldset": { borderColor: "#94A3B8" },
  },
  "& .MuiInputLabel-root": { color: "#475569" },
  "& .Mui-disabled": {
    "& .MuiOutlinedInput-input": {
      color: "#334155 !important",
      WebkitTextFillColor: "#334155 !important",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
  },
};

// Table body cells (only used for the table, not for the date pickers)
const cellSX = {
  fontSize: "0.75rem",
  padding: "8px",
  textAlign: "center",
  borderBottom: "0.5px solid #1b1b1bff",
  borderRight: "0.5px solid #1b1b1bff",
};

const CreditHistory = () => {
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data, isLoading, error } = useGetCreditHistoryQuery({
    fromDate,
    toDate,
    page,
    limit,
  });

  const httpDisabled = data?.status === false;
  const creditHistory = useMemo(
    () => (!httpDisabled ? data?.creditHistory || [] : []),
    [data, httpDisabled]
  );
  const totalPages = useMemo(
    () => (!httpDisabled ? Math.ceil((data?.total || 0) / limit) : 0),
    [data, httpDisabled]
  );

  const backendMessage = data?.message || error?.data?.message || null;

  const handlePageChange = (_, value) => setPage(value);

  return (
    <Box sx={layoutCSS}>
      <Typography variant="h6" sx={{ mb: 1, fontSize: "0.9rem", fontWeight: "bold" }}>
        Credit History
      </Typography>

      {/* ── Glass date filter bar ── */}
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
          p: 2,
          mb: 2,
          borderRadius: "0.5rem",
          backgroundColor: "#ffffff52",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 12px rgba(14,165,233,0.1)",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="From"
            value={dayjs(fromDate)}
            onChange={(date) => {
              setFromDate(date.format("YYYY-MM-DD"));
              setPage(1);
            }}
            maxDate={dayjs()}
            format="DD/MM/YYYY"
            sx={{
              ...inputSx,
              width: 140,
            }}
            slotProps={{ textField: { size: "small" } }}
          />

          <DatePicker
            label="To"
            value={dayjs(toDate)}
            onChange={(date) => {
              setToDate(date.format("YYYY-MM-DD"));
              setPage(1);
            }}
            maxDate={dayjs()}
            minDate={dayjs(fromDate)}
            format="DD/MM/YYYY"
            sx={{
              ...inputSx,
              width: 140,
            }}
            slotProps={{ textField: { size: "small" } }}
          />
        </LocalizationProvider>
      </Paper>

      {/* Loading spinner */}
      {isLoading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && backendMessage && (
        <Typography color="error" align="center" sx={{ mt: 2 }}>
          {backendMessage}
        </Typography>
      )}

      {!isLoading && !backendMessage && (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead
                sx={{
                  bgcolor: "#0288d1",
                  "& th": {
                    color: "white",
                    fontSize: "0.8rem",
                    borderBottom: "0.5px solid #1b1b1bff",
                    borderRight: "0.5px solid #1b1b1bff",
                    p: "6px",
                    textAlign: "center",
                  },
                }}
              >
                <TableRow>
                  <TableCell sx={{ width: "110px" }}>Date</TableCell>
                  <TableCell sx={{ width: "150px" }}>From</TableCell>
                  <TableCell sx={{ width: "150px" }}>To</TableCell>
                  <TableCell sx={{ width: "150px" }}>Credit</TableCell>
                  <TableCell sx={{ width: "150px" }}>Request ID</TableCell>
                  <TableCell>Remark</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {creditHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={cellSX}>
                      No Data Found!
                    </TableCell>
                  </TableRow>
                ) : (
                  creditHistory.map((row) => (
                    <TableRow key={row.transaction_id}>
                      <TableCell sx={cellSX}>
                        {moment(row.date).format("DD/MM/YYYY hh:mm A")}
                      </TableCell>
                      <TableCell sx={cellSX}>{row.fromName}</TableCell>
                      <TableCell sx={cellSX}>{row.toName}</TableCell>
                      <TableCell
                        sx={{
                          ...cellSX,
                          color: row.credit > 0 ? "green" : "inherit",
                        }}
                      >
                        {row.credit > 0 ? `₹${row.credit}` : ""}
                      </TableCell>
                      <TableCell sx={cellSX}>{row.request_id}</TableCell>
                      <TableCell sx={cellSX}>{row.summary}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default CreditHistory;